
import { supabase } from "@/integrations/supabase/client";

interface CategoryMapping {
  byName: Map<string, string>;
  bySlug: Map<string, string>;
  byId: Map<string, string>;
  byKeyword: Map<string, string>;
}

/**
 * Fetches categories from the database and creates category mappings for import operations
 */
export const fetchCategoryMappings = async (): Promise<CategoryMapping> => {
  const { data: categories, error } = await supabase
    .from('categories')
    .select('id, name, slug, parent_id, keywords');
    
  if (error) throw error;
  
  if (!categories || categories.length === 0) {
    throw new Error('No categories found in the database');
  }
  
  const categoryByName = new Map();
  const categoryBySlug = new Map();
  const categoryById = new Map();
  const categoryByKeyword = new Map();
  
  categories.forEach((cat) => {
    categoryByName.set(cat.name.toLowerCase(), cat.id);
    categoryBySlug.set(cat.slug.toLowerCase(), cat.id);
    categoryById.set(cat.id, cat.id);
    
    if (cat.keywords && Array.isArray(cat.keywords)) {
      cat.keywords.forEach(keyword => {
        categoryByKeyword.set(keyword.toLowerCase(), cat.id);
      });
    }
  });
  
  return {
    byName: categoryByName,
    bySlug: categoryBySlug,
    byId: categoryById,
    byKeyword: categoryByKeyword
  };
};

/**
 * Resolves a category input to a category ID using the provided mappings
 */
export const resolveCategoryId = (
  categoryInput: string | undefined,
  categoryMapping: CategoryMapping
): string | null => {
  if (!categoryInput?.trim()) {
    return null;
  }
  
  const input = categoryInput.trim();
  
  if (categoryMapping.byId.has(input)) {
    return categoryMapping.byId.get(input) || null;
  }
  
  const possibleSlug = input.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  if (categoryMapping.bySlug.has(possibleSlug)) {
    return categoryMapping.bySlug.get(possibleSlug) || null;
  }
  
  const lowerName = input.toLowerCase();
  if (categoryMapping.byName.has(lowerName)) {
    return categoryMapping.byName.get(lowerName) || null;
  }
  
  if (categoryMapping.byKeyword.has(lowerName)) {
    return categoryMapping.byKeyword.get(lowerName) || null;
  }
  
  return null;
};
